import axios from "axios";
import JSZip from "jszip";

const BASE = `${process.env.REACT_APP_API_URL}/marksheet-template`;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MarksheetTemplateApiRecord {
  id: number;
  classId: number;
  sessionId: number;
  className: string;
  sessionName: string;
  htmlFile: string;
  cssFile: string;
  createdBy: number;
  updatedBy: number;
  templateName: string;
  primary: boolean;
  createdAt: number[];
  updatedAt: number[];
}

// ── API 1: List templates for a class+session ──────────────────────────────────

export async function listMarksheetTemplatesFromApi(
  className: string,
  sessionId: number
): Promise<MarksheetTemplateApiRecord[]> {
  const res = await axios.get<MarksheetTemplateApiRecord[]>(
    `${BASE}/${encodeURIComponent(className)}/${sessionId}`
  );
  return res.data ?? [];
}

// ── API 2: Create template ─────────────────────────────────────────────────────

export interface CreateMarksheetTemplateParams {
  classId: number;
  className: string;
  sessionId: number;
  sessionName: string;
  createdBy: number;
  updatedBy: number;
  templateName: string;
  primary: boolean;
  htmlContent: string;
  cssContent: string;
}

export async function createMarksheetTemplate(
  params: CreateMarksheetTemplateParams
): Promise<MarksheetTemplateApiRecord> {
  const { htmlContent, cssContent, ...meta } = params;

  const metaBlob = new Blob([JSON.stringify(meta)], { type: "application/json" });
  const htmlBlob = new Blob([htmlContent], { type: "text/html" });
  const cssBlob = new Blob([cssContent], { type: "text/css" });

  const form = new FormData();
  form.append("customMarksheetTemplate", metaBlob);
  form.append("htmlFile", htmlBlob, "template.html");
  form.append("cssFile", cssBlob, "template.css");

  const res = await axios.post<MarksheetTemplateApiRecord>(BASE, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// ── API 3: Update template ─────────────────────────────────────────────────────

export interface UpdateMarksheetTemplateParams extends CreateMarksheetTemplateParams {
  id: number;
}

export async function updateMarksheetTemplate(
  params: UpdateMarksheetTemplateParams
): Promise<MarksheetTemplateApiRecord> {
  const { htmlContent, cssContent, ...meta } = params;

  const metaBlob = new Blob([JSON.stringify(meta)], { type: "application/json" });
  const htmlBlob = new Blob([htmlContent], { type: "text/html" });
  const cssBlob = new Blob([cssContent], { type: "text/css" });

  const form = new FormData();
  form.append("customMarksheetTemplate", metaBlob);
  form.append("htmlFile", htmlBlob, "template.html");
  form.append("cssFile", cssBlob, "template.css");

  const res = await axios.put<MarksheetTemplateApiRecord>(BASE, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// ── API 4: Set template as primary ─────────────────────────────────────────────

export async function setPrimaryMarksheetTemplate(
  id: number
): Promise<MarksheetTemplateApiRecord> {
  const res = await axios.put<MarksheetTemplateApiRecord>(`${BASE}/${id}`);
  return res.data;
}

// ── API 5: Delete template ─────────────────────────────────────────────────────

export async function deleteMarksheetTemplate(id: number): Promise<void> {
  await axios.delete(`${BASE}/${id}`);
}

// ── API 6: Download HTML + CSS files (ZIP) ─────────────────────────────────────

export interface MarksheetTemplateFiles {
  htmlContent: string;
  cssContent: string;
}

export async function getMarksheetTemplateFiles(
  htmlFile: string,
  cssFile: string
): Promise<MarksheetTemplateFiles> {
  const res = await axios.post<ArrayBuffer>(
    `${BASE}/getFiles`,
    null,
    {
      params: { htmlFile, cssFile },
      responseType: "arraybuffer",
    }
  );

  const zip = await JSZip.loadAsync(res.data);

  let htmlContent = "";
  let cssContent = "";

  await Promise.all(
    Object.keys(zip.files).map(async (name) => {
      const file = zip.files[name];
      if (file.dir) return;
      const text = await file.async("string");
      if (name.endsWith(".html") || name === htmlFile) {
        htmlContent = text;
      } else if (name.endsWith(".css") || name === cssFile) {
        cssContent = text;
      }
    })
  );

  if (!htmlContent && !cssContent) {
    const files = Object.values(zip.files).filter((f) => !f.dir);
    if (files[0]) htmlContent = await files[0].async("string");
    if (files[1]) cssContent = await files[1].async("string");
  }

  return { htmlContent, cssContent };
}

// ── Helper ─────────────────────────────────────────────────────────────────────

export function springDateToTs(arr: number[]): number {
  if (!arr || arr.length < 3) return 0;
  return new Date(arr[0], arr[1] - 1, arr[2]).getTime();
}
